import { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import {
  ChevronLeft,
  Mail,
  Briefcase,
  Building,
  Pencil,
  Circle,
  Clock,
  AlertTriangle,
  CheckSquare,
  X,
  MessageSquare,
  Calendar,
  Hourglass,
} from 'lucide-react';
import { apiGet, apiPatch } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { PinButton } from '../components/PinButton';
import type { EntityType } from '../components/EntityChip';

/* ── Types ─────────────────────────────────────────────── */

interface Person {
  id: string;
  name: string;
  email: string | null;
  role: string | null;
  department: string | null;
  created_at: string;
  updated_at: string;
}

interface Task {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  tag_ids: string[];
  scope_ids: string[];
  created_at: string;
}

interface TagItem {
  id: string;
  name: string;
  color: string;
}

/* ── Constants ─────────────────────────────────────────── */

const STATUS_ICONS: Record<string, React.ReactNode> = {
  todo: <Circle size={14} className="text-text-muted" />,
  in_progress: <Clock size={14} className="text-accent" />,
  blocked: <AlertTriangle size={14} className="text-danger" />,
  done: <CheckSquare size={14} className="text-success" />,
  cancelled: <X size={14} className="text-text-muted" />,
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'text-danger',
  high: 'text-warning',
  medium: 'text-text-secondary',
  low: 'text-text-muted',
};

/* ── Main Component ───────────────────────────────────── */

export function PersonDetailPage() {
  const { personId } = useParams<{ personId: string }>();
  const location = useLocation();
  const toast = useToast();
  const navState = location.state as { from?: string; fromLabel?: string } | null;
  const [person, setPerson] = useState<Person | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tags, setTags] = useState<TagItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Edit modal state
  const [showEdit, setShowEdit] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<'tasks' | 'meetings' | 'waiting' | 'mentions'>('tasks');
  const [meetingLinks, setMeetingLinks] = useState<{ id: string; name: string; date: string }[]>([]);
  const [waitingOnTasks, setWaitingOnTasks] = useState<Task[]>([]);

  async function loadData() {
    if (!personId) return;
    try {
      const [p, t, allTags] = await Promise.all([
        apiGet<Person>(`/people/${personId}`),
        apiGet<Task[]>(`/tasks?assignee_id=${personId}&sort_by=created_at&sort_dir=desc`),
        apiGet<TagItem[]>('/tags'),
      ]);
      setPerson(p);
      setTasks(t);
      setTags(allTags);

      // Load entity links to this person
      try {
        const links = await apiGet<{ id: string; source_type: EntityType; source_id: string; relationship: string }[]>(
          `/entity-links?target_type=person&target_id=${personId}`
        );

        // Meetings attended
        const attendeeLinks = links.filter(l => l.source_type === 'meeting_instance' && l.relationship === 'attendee');
        const meetings: { id: string; name: string; date: string }[] = [];
        for (const link of attendeeLinks.slice(0, 20)) {
          try {
            const m = await apiGet<{ id: string; title: string | null; series_name: string; meeting_date: string }>(`/meeting-instances/${link.source_id}`);
            meetings.push({ id: m.id, name: m.title ?? m.series_name, date: m.meeting_date });
          } catch { /* meeting may have been deleted */ }
        }
        meetings.sort((a, b) => b.date.localeCompare(a.date));
        setMeetingLinks(meetings);

        // Tasks waiting on this person
        const waitingLinks = links.filter(l => l.source_type === 'task' && l.relationship === 'waiting_on');
        const waitingTasks: Task[] = [];
        for (const link of waitingLinks.slice(0, 20)) {
          try {
            const task = await apiGet<Task>(`/tasks/${link.source_id}`);
            waitingTasks.push(task);
          } catch { /* task may have been deleted */ }
        }
        setWaitingOnTasks(waitingTasks);
      } catch { /* entity links not critical */ }
    } catch (err) {
      console.error('Failed to load person:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, [personId]);

  async function handleUpdatePerson(data: Partial<Person>) {
    if (!personId) return;
    try {
      const updated = await apiPatch<Person>(`/people/${personId}`, data);
      setPerson(updated);
      setShowEdit(false);
      toast.success('Person updated');
    } catch (err) {
      console.error('Failed to update person:', err);
      toast.error('Failed to update person');
    }
  }

  /* ── Render ─────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <Link to={navState?.from ?? '/people'} className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors mb-4">
          <ChevronLeft size={16} /> {navState?.fromLabel ?? 'Back to People'}
        </Link>
        <div className="text-text-muted text-center py-16">Person not found.</div>
      </div>
      </div>
    );
  }

  const tagMap = new Map(tags.map(t => [t.id, t]));

  const taskStats = {
    total: tasks.length,
    open: tasks.filter(t => t.status !== 'done' && t.status !== 'cancelled').length,
    done: tasks.filter(t => t.status === 'done').length,
  };

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-4xl mx-auto">
      {/* Back nav */}
      <div className="flex items-center gap-3 mb-4">
        <Link
          to={navState?.from ?? '/people'}
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors"
        >
          <ChevronLeft size={16} /> {navState?.fromLabel ?? 'Back to People'}
        </Link>
        {navState?.from && navState.from !== '/people' && (
          <Link
            to="/people"
            className="text-xs text-text-muted hover:text-text-secondary transition-colors"
          >
            All People
          </Link>
        )}
      </div>

      {/* Person Header Card */}
      <div className="bg-bg-card border border-border rounded-lg p-6 mb-6">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-16 h-16 rounded-full bg-accent/20 text-accent flex items-center justify-center text-2xl font-bold flex-shrink-0">
            {person.name.charAt(0).toUpperCase()}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-text-primary">{person.name}</h1>
              <button
                onClick={() => setShowEdit(true)}
                className="p-1.5 text-text-muted hover:text-text-primary hover:bg-bg-hover rounded transition-colors"
                title="Edit person"
              >
                <Pencil size={16} />
              </button>
              <PinButton entityType="person" entityId={personId!} label={person.name} />
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-text-secondary">
              {person.email && (
                <span className="flex items-center gap-1.5">
                  <Mail size={14} className="text-text-muted" />
                  <a href={`mailto:${person.email}`} className="hover:text-accent transition-colors">{person.email}</a>
                </span>
              )}
              {person.role && (
                <span className="flex items-center gap-1.5">
                  <Briefcase size={14} className="text-text-muted" />
                  {person.role}
                </span>
              )}
              {person.department && (
                <span className="flex items-center gap-1.5">
                  <Building size={14} className="text-text-muted" />
                  {person.department}
                </span>
              )}
            </div>
            {/* Quick stats */}
            <div className="flex items-center gap-4 mt-3 text-xs text-text-muted">
              <span>{taskStats.total} task{taskStats.total !== 1 ? 's' : ''} assigned</span>
              <span>{taskStats.open} open</span>
              <span>{taskStats.done} completed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border mb-4">
        <button
          onClick={() => setActiveTab('tasks')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'tasks'
              ? 'border-accent text-accent'
              : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          Assigned Tasks ({taskStats.total})
        </button>
        <button
          onClick={() => setActiveTab('meetings')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'meetings'
              ? 'border-accent text-accent'
              : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          Meetings ({meetingLinks.length})
        </button>
        <button
          onClick={() => setActiveTab('waiting')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'waiting'
              ? 'border-accent text-accent'
              : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          Waiting On ({waitingOnTasks.length})
        </button>
        <button
          onClick={() => setActiveTab('mentions')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'mentions'
              ? 'border-accent text-accent'
              : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          Mentions
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'tasks' && (
        <div>
          {tasks.length === 0 ? (
            <div className="text-center py-12 text-text-muted">
              <p className="text-sm">No tasks assigned to this person.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {tasks.map(task => (
                <Link
                  key={task.id}
                  to={`/tasks/${task.id}`}
                  state={person ? { from: `/people/${personId}`, fromLabel: person.name } : undefined}
                  className="flex items-center gap-3 px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors group block"
                >
                  <span className="flex-shrink-0" title={task.status}>
                    {STATUS_ICONS[task.status] ?? <Circle size={14} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm ${task.status === 'done' ? 'line-through text-text-muted' : 'text-text-primary group-hover:text-accent'}`}>
                      {task.title}
                    </span>
                    <div className="flex items-center gap-2 mt-0.5">
                      {task.tag_ids.map(tid => {
                        const tag = tagMap.get(tid);
                        return tag ? (
                          <span
                            key={tid}
                            className="text-xs px-1.5 py-0.5 rounded-full"
                            style={{ backgroundColor: tag.color + '20', color: tag.color }}
                          >
                            {tag.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {task.due_date && (
                      <span className={`text-xs ${isOverdue(task.due_date) && task.status !== 'done' ? 'text-danger' : 'text-text-muted'}`}>
                        {formatShortDate(task.due_date)}
                      </span>
                    )}
                    <span className={`text-xs capitalize ${PRIORITY_COLORS[task.priority]}`}>
                      {task.priority}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'meetings' && (
        <div>
          {meetingLinks.length === 0 ? (
            <div className="text-center py-12 text-text-muted">
              <Calendar size={40} className="mx-auto mb-3 opacity-50" />
              <p className="text-sm">No meetings attended.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {meetingLinks.map(m => (
                <Link
                  key={m.id}
                  to={`/meetings/${m.id}`}
                  className="flex items-center gap-3 px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors"
                >
                  <Calendar size={14} className="text-warning flex-shrink-0" />
                  <span className="text-sm text-text-primary flex-1">{m.name}</span>
                  <span className="text-xs text-text-muted">{formatShortDate(m.date)}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'waiting' && (
        <div>
          {waitingOnTasks.length === 0 ? (
            <div className="text-center py-12 text-text-muted">
              <Hourglass size={40} className="mx-auto mb-3 opacity-50" />
              <p className="text-sm">No tasks waiting on this person.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {waitingOnTasks.map(task => (
                <Link
                  key={task.id}
                  to={`/tasks/${task.id}`}
                  state={person ? { from: `/people/${personId}`, fromLabel: person.name } : undefined}
                  className="flex items-center gap-3 px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors group"
                >
                  <span className="flex-shrink-0" title={task.status}>
                    {STATUS_ICONS[task.status] ?? <Circle size={14} />}
                  </span>
                  <span className={`text-sm flex-1 ${task.status === 'done' ? 'line-through text-text-muted' : 'text-text-primary group-hover:text-accent'}`}>
                    {task.title}
                  </span>
                  {task.due_date && (
                    <span className={`text-xs ${isOverdue(task.due_date) && task.status !== 'done' ? 'text-danger' : 'text-text-muted'}`}>
                      {formatShortDate(task.due_date)}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'mentions' && (
        <div className="text-center py-12 text-text-muted">
          <MessageSquare size={40} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">Mentions are not yet available.</p>
          <p className="text-xs mt-1">This section will show notes and meetings where this person is @mentioned.</p>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && (
        <EditPersonModal
          person={person}
          onSave={handleUpdatePerson}
          onClose={() => setShowEdit(false)}
        />
      )}
    </div>
    </div>
  );
}

/* ── Edit Person Modal ────────────────────────────────── */

function EditPersonModal({
  person,
  onSave,
  onClose,
}: {
  person: Person;
  onSave: (data: Partial<Person>) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(person.name);
  const [email, setEmail] = useState(person.email ?? '');
  const [role, setRole] = useState(person.role ?? '');
  const [department, setDepartment] = useState(person.department ?? '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">Edit Person</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Name *</label>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="Full name"
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Email</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@company.com"
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Role</label>
              <input
                type="text" value={role} onChange={e => setRole(e.target.value)} placeholder="e.g., Manager"
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Department</label>
              <input
                type="text" value={department} onChange={e => setDepartment(e.target.value)} placeholder="e.g., Engineering"
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">Cancel</button>
          <button
            onClick={() => name.trim() && onSave({
              name: name.trim(),
              email: email || null,
              role: role || null,
              department: department || null,
            })}
            disabled={!name.trim()}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Utility Functions ────────────────────────────────── */

function formatShortDate(d: string): string {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isOverdue(d: string): boolean {
  return new Date(d + 'T23:59:59') < new Date();
}
